import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { band, journeyFrames } from '@/constants/portfolio'
import { useFrame } from '@/hooks/use-frame'
import { clamp, documentTop } from '@/lib/motion'
import { useExperience } from '@/components/generals/experience-provider'
import InstrumentArt from '@/components/landing/instrument-art'
import JourneyFrame from '@/components/landing/journey-frame'

const Intro: FC<{ wide?: boolean }> = ({ wide }): ReactNode => (
    <div className={cn('relative flex shrink-0 flex-col justify-end overflow-hidden', wide ? 'w-[min(44vw,720px)] pr-[3vw]' : 'w-full')}>
        {wide && (
            <div aria-hidden='true' className='pointer-events-none absolute -right-[4vw] top-[4%] w-[46%] rotate-[8deg] opacity-30'>
                <InstrumentArt kind='mic' flat />
            </div>
        )}

        <p className='mono-label relative text-[11px] text-night-text'>04 / The journey</p>
        <h2 id='journey-title' className='display-type relative mt-3 text-[clamp(3rem,7vw,7rem)] text-paper'>Journey</h2>
        <p className='relative mt-6 max-w-[34ch] text-[clamp(1.15rem,1.7vw,1.6rem)] font-medium leading-[1.42] text-paper'>
            {band.journeyText}
        </p>
        <p className='mono-label relative mt-6 text-[10px] text-night-text'>
            {wide ? 'Keep scrolling, the strip runs sideways' : 'Swipe through the frames'}
        </p>
    </div>
)

// Live journey as a film strip. On desktop vertical scroll drives horizontal travel;
// on phones and in reduced motion it becomes a native swipeable row.
const JourneySection: FC = (): ReactNode => {
    const { pinned } = useExperience()
    const wrapRef = useRef<HTMLElement>(null)
    const stripRef = useRef<HTMLDivElement>(null)
    const barRef = useRef<HTMLSpanElement>(null)
    const artRefs = useRef<Array<HTMLDivElement | null>>([])
    const centers = useRef<number[]>([])
    const measure = useRef({ at: -1, top: 0, travel: 1, distance: 0 })
    const [height, setHeight] = useState<number | null>(null)

    useEffect(() => {
        if (!pinned) return

        const strip = stripRef.current
        if (!strip) return

        const update = () => {
            const distance = Math.max(0, strip.scrollWidth - window.innerWidth)
            setHeight(Math.round(window.innerHeight + distance))
            centers.current = Array.from(strip.children).map(child => {
                const element = child as HTMLElement
                return element.offsetLeft + element.offsetWidth / 2
            })
            measure.current.at = -1
        }

        update()

        const observer = new ResizeObserver(update)
        observer.observe(strip)
        window.addEventListener('resize', update)

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', update)
        }
    }, [pinned])

    useFrame(state => {
        const wrap = wrapRef.current
        const strip = stripRef.current
        if (!wrap || !strip) return

        const vh = state.viewportH
        const vw = state.viewportW

        if (measure.current.at < 0 || state.time - measure.current.at > 0.5) {
            measure.current = {
                at: state.time,
                top: documentTop(wrap),
                travel: Math.max(1, wrap.offsetHeight - vh),
                distance: Math.max(0, strip.scrollWidth - vw)
            }
        }

        const { top, travel, distance } = measure.current
        if (state.scrollY < top - vh * 1.2 || state.scrollY > top + travel + vh * 1.2) return

        const p = clamp((state.scrollY - top) / travel)
        const x = -p * distance

        strip.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0)`
        if (barRef.current) barRef.current.style.transform = `scaleX(${p.toFixed(4)})`

        artRefs.current.forEach((element, index) => {
            if (!element) return

            const center = (centers.current[index + 1] ?? 0) + x
            const offset = Math.max(-1.4, Math.min(1.4, (center - vw / 2) / vw))
            element.style.transform = `translate3d(${(offset * -46).toFixed(1)}px, ${(Math.abs(offset) * 10).toFixed(1)}px, 0) rotate(${(offset * -4).toFixed(2)}deg)`
        })
    }, pinned)

    if (!pinned) {
        return (
            <section id='journey' data-theme='night' aria-labelledby='journey-title' className='relative overflow-hidden bg-night py-20 tab:py-28'>
                <div className='px-5 xs:px-8 tab:px-12'>
                    <Intro />
                </div>

                <div
                    role='region'
                    aria-label='Journey frames, scroll sideways'
                    tabIndex={0}
                    className='mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-6 outline-offset-[-3px] xs:px-8 tab:px-12'
                >
                    {journeyFrames.map(frame => (
                        <JourneyFrame key={frame.id} frame={frame} className='h-[min(560px,78svh)] w-[min(82vw,420px)] snap-center' />
                    ))}
                </div>
            </section>
        )
    }

    return (
        <section
            ref={wrapRef}
            id='journey'
            data-theme='night'
            data-pin-wrapper='true'
            aria-labelledby='journey-title'
            className='relative h-[calc(100vh+260vw)] bg-night'
            style={height ? { height } : undefined}
        >
            <div className='sticky top-0 flex h-[100svh] flex-col justify-center overflow-hidden pt-16'>
                <div ref={stripRef} className='flex h-[min(76vh,760px)] items-stretch gap-[2.2vw] pl-12 pr-[10vw] will-change-transform'>
                    <Intro wide />

                    {journeyFrames.map((frame, index) => (
                        <JourneyFrame
                            key={frame.id}
                            frame={frame}
                            artRef={element => {
                                artRefs.current[index] = element
                            }}
                            className='w-[min(30vw,480px)]'
                        />
                    ))}
                </div>

                <div className='absolute inset-x-12 bottom-[4vh] flex items-center gap-4'>
                    <span className='mono-label text-[10px] text-night-text'>01</span>
                    <span className='relative block h-[3px] flex-1 bg-paper/15' aria-hidden='true'>
                        <span ref={barRef} className='absolute inset-0 origin-left bg-paper' style={{ transform: 'scaleX(0)' }} />
                    </span>
                    <span className='mono-label text-[10px] text-night-text'>{String(journeyFrames.length).padStart(2, '0')}</span>
                </div>
            </div>
        </section>
    )
}

export default JourneySection
